
import React, { useState } from "react";
import { Box, ClickAwayListener, CircularProgress, InputBase } from "@mui/material";
import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";
import { useTranslation } from "react-i18next";
import { useTheme } from "../contexts/ThemeContext";

const CustomDropdownInput = ({
  label,
  required = false,
  placeholder,
  value,
  onChange,
  options = [],
  getOptionLabel = (opt) => opt.label,
  getOptionValue = (opt) => opt.value,
  disabled = false,
  loading = false,
  loadingText = "",
  enableSearch = false, // New prop to enable search
}) => {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState(""); // Search query state
  const { primaryColor } = useTheme();
  const { t } = useTranslation();

  const handleSelect = (option) => {
    if (disabled || loading) return;
    onChange(getOptionValue(option));
    setOpen(false);
  };

  const selectedOption = options.find(
    (opt) => getOptionValue(opt) === value
  );

  const filteredOptions = enableSearch
    ? options.filter((opt) =>
        getOptionLabel(opt).toLowerCase().includes(search.toLowerCase())
      )
    : options; // Filter options based on search query

  return (
    <ClickAwayListener onClickAway={() => !disabled && !loading && setOpen(false)}>
      <Box sx={{ position: "relative", mt: 1.5 }}>
        <label style={{ color: "#626F86", fontSize: 14 }}>
          {label} {required && <span style={{ color: "red" }}>*</span>}
        </label>

        <Box
          className="project_input"
          onClick={() => !disabled && !loading && setOpen(!open)}
          sx={{
            mt: 0.5,
            height: 38,
            borderRadius: 2,
            px: 1.5,
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            cursor: disabled || loading ? "not-allowed" : "pointer",
            border: `2px solid ${primaryColor}20`,
            borderRadius: "6px",
            fontSize: 14,
            fontFamily: "Poppins, sans-serif",
            opacity: disabled || loading ? 0.6 : 1,
          }}
        >
          <span style={{ color: selectedOption ? "#000" : "#aaa" }}>
            {loading ? (
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <CircularProgress size={14} sx={{ color: "#006EC2" }} />
               
              </Box>
            ) : selectedOption ? (
              getOptionLabel(selectedOption)
            ) : (
              t("select")
            )}
          </span>
          <ArrowDropDownIcon />
        </Box>

        {open && !disabled && !loading && (
          <Box
            sx={{
              position: "absolute",
              top: "100%",
              left: 0,
              right: 0,
              mt: 0.5,
              border: `1px solid ${primaryColor}40`,
              borderRadius: "6px",
              backgroundColor: "#fff",
              maxHeight: 200,
              overflowY: "auto",
              zIndex: 10,
              boxShadow: "0 2px 8px rgba(0,0,0,0.15)",
            }}
          >
            {enableSearch && (
              <Box sx={{ px: 2, py: 1 }}>
                <InputBase
                  autoFocus
                  placeholder={t("searchPlaceholder")}
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  sx={{
                    width: "100%",
                    border: "1px solid #ccc",
                    borderRadius: "6px",
                    px: 1,
                    py: 0.7,
                    fontSize: "14px",
                    fontFamily: "Poppins, sans-serif",
                    "&:focus": {
                      borderColor: "#1976d2",
                      boxShadow: "0 0 0 2px rgba(25,118,210,0.2)",
                    },
                  }}
                />
              </Box>
            )}

            {loading ? (
              <Box
                sx={{
                  px: 2,
                  py: 2,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  gap: 1,
                }}
              >
                <CircularProgress size={16} sx={{ color: "#006EC2" }} />
                <span style={{ fontSize: "14px", fontFamily: "Poppins, sans-serif" }}>
                  {typeof loadingText === 'string' ? loadingText : "Loading..."}
                </span>
              </Box>
            ) : filteredOptions.length > 0 ? (
              filteredOptions.map((opt, idx) => (
                <React.Fragment key={getOptionValue(opt)}>
                  <Box
                    sx={{
                      px: 2,
                      py: 1,
                      cursor: "pointer",
                      "&:hover": { backgroundColor: `${primaryColor}10` },
                    }}
                    onClick={() => handleSelect(opt)}
                  >
                    {getOptionLabel(opt)}
                  </Box>
                  {idx < filteredOptions.length - 1 && (
                    <Box sx={{ borderTop: `1px solid ${primaryColor}20` }} />
                  )}
                </React.Fragment>
              ))
            ) : (
              <Box sx={{ px: 2, py: 2, color: "#aaa", fontSize: 14 }}>
                {t("noOption")}
              </Box>
            )}
          </Box>
        )}
      </Box>
    </ClickAwayListener>
  );
};

export default CustomDropdownInput;



